import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Server, Cloud, Key, Network, Settings } from 'lucide-react';

export function EnhancedSAPSystemConfig({ open, onOpenChange, system, onSave }) {
  const [formData, setFormData] = useState(system || {
    name: '',
    type: 'S/4HANA Cloud',
    architecture_type: 'single_system_multi_client',
    ...system
  });

  const updateField = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const updateAccessType = (accessType, checked) => {
    setFormData({
      ...formData,
      access_types: {
        ...formData.access_types,
        [accessType]: checked
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Server className="h-5 w-5" />
            Configure SAP System for Real Deployment
          </DialogTitle>
          <DialogDescription>
            Provide complete configuration for DEV → QA → PROD deployment
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="grid grid-cols-5 w-full">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="architecture">Architecture</TabsTrigger>
            <TabsTrigger value="access">Access & APIs</TabsTrigger>
            <TabsTrigger value="credentials">Credentials</TabsTrigger>
            <TabsTrigger value="transport">Transport</TabsTrigger>
          </TabsList>

          {/* Basic Info Tab */}
          <TabsContent value="basic" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>System Name *</Label>
                <Input
                  value={formData.name || ''}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="e.g., RTS S4-D Production"
                />
              </div>
              <div>
                <Label>System Type *</Label>
                <Select value={formData.type} onValueChange={(v) => updateField('type', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="S/4HANA Cloud">S/4HANA Cloud</SelectItem>
                    <SelectItem value="S/4HANA On-Premise">S/4HANA On-Premise</SelectItem>
                    <SelectItem value="SAP ECC">SAP ECC</SelectItem>
                    <SelectItem value="SAP BTP">SAP BTP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Version</Label>
                <Input
                  value={formData.version || ''}
                  onChange={(e) => updateField('version', e.target.value)}
                  placeholder="e.g., 2023, ECC 6.0 EHP8"
                />
              </div>
              <div>
                <Label>Location</Label>
                <Select value={formData.location || 'US-East'} onValueChange={(v) => updateField('location', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="US-East">US-East</SelectItem>
                    <SelectItem value="US-West">US-West</SelectItem>
                    <SelectItem value="EU-Central">EU-Central</SelectItem>
                    <SelectItem value="Asia-Pacific">Asia-Pacific</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </TabsContent>

          {/* Architecture Tab */}
          <TabsContent value="architecture" className="space-y-4 mt-4">
            <div>
              <Label>How are DEV, QA, PROD environments separated? *</Label>
              <Select value={formData.architecture_type || 'single_system_multi_client'} 
                      onValueChange={(v) => updateField('architecture_type', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="single_system_multi_client">
                    Single System with Multiple Clients (e.g., Client 100=DEV, 200=QA, 300=PROD)
                  </SelectItem>
                  <SelectItem value="separate_systems">
                    Separate Physical Systems (Different URLs for each)
                  </SelectItem>
                  <SelectItem value="btp_spaces">
                    SAP BTP with Multiple Spaces (dev-space, qa-space, prod-space)
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.architecture_type === 'single_system_multi_client' && (
              <>
                <div>
                  <Label>System URL</Label>
                  <Input
                    value={formData.url || ''}
                    onChange={(e) => updateField('url', e.target.value)}
                    placeholder="https://your-sap-system.com"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>DEV Client</Label>
                    <Input
                      value={formData.dev_client || '100'}
                      onChange={(e) => updateField('dev_client', e.target.value)}
                      placeholder="100"
                    />
                  </div>
                  <div>
                    <Label>QA Client</Label>
                    <Input
                      value={formData.qa_client || '200'}
                      onChange={(e) => updateField('qa_client', e.target.value)}
                      placeholder="200"
                    />
                  </div>
                  <div>
                    <Label>PROD Client</Label>
                    <Input
                      value={formData.prod_client || '300'}
                      onChange={(e) => updateField('prod_client', e.target.value)}
                      placeholder="300"
                    />
                  </div>
                </div>
              </>
            )}

            {formData.architecture_type === 'separate_systems' && (
              <div className="space-y-3">
                <div>
                  <Label>DEV System URL</Label>
                  <Input
                    value={formData.dev_url || ''}
                    onChange={(e) => updateField('dev_url', e.target.value)}
                    placeholder="https://dev-sap.company.com"
                  />
                </div>
                <div>
                  <Label>QA System URL</Label>
                  <Input
                    value={formData.qa_url || ''}
                    onChange={(e) => updateField('qa_url', e.target.value)}
                    placeholder="https://qa-sap.company.com"
                  />
                </div>
                <div>
                  <Label>PROD System URL</Label>
                  <Input
                    value={formData.prod_url || ''}
                    onChange={(e) => updateField('prod_url', e.target.value)}
                    placeholder="https://prod-sap.company.com"
                  />
                </div>
              </div>
            )}

            {formData.architecture_type === 'btp_spaces' && (
              <div className="space-y-3">
                <div>
                  <Label>BTP API Endpoint</Label>
                  <Input
                    value={formData.cf_api_endpoint || ''}
                    onChange={(e) => updateField('cf_api_endpoint', e.target.value)}
                    placeholder="https://api.cf.us10.hana.ondemand.com"
                  />
                </div>
                <div>
                  <Label>BTP Organization</Label>
                  <Input
                    value={formData.cf_org || ''}
                    onChange={(e) => updateField('cf_org', e.target.value)}
                    placeholder="your-org-name"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>DEV Space</Label>
                    <Input
                      value={formData.cf_space_dev || ''}
                      onChange={(e) => updateField('cf_space_dev', e.target.value)}
                      placeholder="dev-space"
                    />
                  </div>
                  <div>
                    <Label>QA Space</Label>
                    <Input
                      value={formData.cf_space_qa || ''}
                      onChange={(e) => updateField('cf_space_qa', e.target.value)}
                      placeholder="qa-space"
                    />
                  </div>
                  <div>
                    <Label>PROD Space</Label>
                    <Input
                      value={formData.cf_space_prod || ''}
                      onChange={(e) => updateField('cf_space_prod', e.target.value)}
                      placeholder="prod-space"
                    />
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Access & APIs Tab */}
          <TabsContent value="access" className="space-y-4 mt-4">
            <div>
              <Label className="mb-3 block">Available Access Methods (Check all that apply)</Label>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="http_rest"
                    checked={formData.access_types?.http_rest || false}
                    onCheckedChange={(checked) => updateAccessType('http_rest', checked)}
                  />
                  <Label htmlFor="http_rest" className="font-normal">
                    HTTP/REST API Access
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="odata_api"
                    checked={formData.access_types?.odata_api || false}
                    onCheckedChange={(checked) => updateAccessType('odata_api', checked)}
                  />
                  <Label htmlFor="odata_api" className="font-normal">
                    OData API (Gateway Services)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="cf_api"
                    checked={formData.access_types?.cloud_foundry_api || false}
                    onCheckedChange={(checked) => updateAccessType('cloud_foundry_api', checked)}
                  />
                  <Label htmlFor="cf_api" className="font-normal">
                    Cloud Foundry API (BTP Deployment)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="adt_api"
                    checked={formData.access_types?.adt_api || false}
                    onCheckedChange={(checked) => updateAccessType('adt_api', checked)}
                  />
                  <Label htmlFor="adt_api" className="font-normal">
                    ABAP Development Tools (ADT) API
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="rfc_access"
                    checked={formData.access_types?.rfc_access || false}
                    onCheckedChange={(checked) => updateAccessType('rfc_access', checked)}
                  />
                  <Label htmlFor="rfc_access" className="font-normal">
                    RFC/BAPI Access
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="transport_api"
                    checked={formData.access_types?.transport_api || false}
                    onCheckedChange={(checked) => updateAccessType('transport_api', checked)}
                  />
                  <Label htmlFor="transport_api" className="font-normal">
                    Transport Management API (CTS+)
                  </Label>
                </div>
              </div>
            </div>

            {formData.access_types?.odata_api && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                <Label>OData Base URL</Label>
                <Input
                  value={formData.odata_base_url || ''}
                  onChange={(e) => updateField('odata_base_url', e.target.value)}
                  placeholder="https://your-sap-system.com/sap/opu/odata"
                />
              </div>
            )}
          </TabsContent>

          {/* Credentials Tab */}
          <TabsContent value="credentials" className="space-y-4 mt-4">
            <Alert>
              <AlertDescription className="text-xs">
                <strong>Security:</strong> All credentials are encrypted and stored securely. They are only used for automated deployment.
              </AlertDescription>
            </Alert>

            <div className="space-y-4">
              <div>
                <Label className="font-semibold">SAP System Credentials</Label>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div>
                    <Label>Username</Label>
                    <Input
                      value={formData.username || ''}
                      onChange={(e) => updateField('username', e.target.value)}
                      placeholder="SAP username"
                    />
                  </div>
                  <div>
                    <Label>Password</Label>
                    <Input
                      type="password"
                      value={formData.password || ''}
                      onChange={(e) => updateField('password', e.target.value)}
                      placeholder="••••••••"
                    />
                  </div>
                </div>
              </div>

              {formData.access_types?.cloud_foundry_api && (
                <div>
                  <Label className="font-semibold">Cloud Foundry Credentials</Label>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div>
                      <Label>CF Username/Email</Label>
                      <Input
                        value={formData.cf_username || ''}
                        onChange={(e) => updateField('cf_username', e.target.value)}
                        placeholder="cf-user@company.com"
                      />
                    </div>
                    <div>
                      <Label>CF Password</Label>
                      <Input
                        type="password"
                        value={formData.cf_password || ''}
                        onChange={(e) => updateField('cf_password', e.target.value)}
                        placeholder="••••••••"
                      />
                    </div>
                  </div>
                </div>
              )}

              {formData.access_types?.odata_api && (
                <div>
                  <Label className="font-semibold">OData API Credentials</Label>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div>
                      <Label>API Username</Label>
                      <Input
                        value={formData.api_username || ''}
                        onChange={(e) => updateField('api_username', e.target.value)}
                        placeholder="API user"
                      />
                    </div>
                    <div>
                      <Label>API Password</Label>
                      <Input
                        type="password"
                        value={formData.api_password || ''}
                        onChange={(e) => updateField('api_password', e.target.value)}
                        placeholder="••••••••"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Transport Tab */}
          <TabsContent value="transport" className="space-y-4 mt-4">
            <div>
              <Label>Transport Domain (for CTS+)</Label>
              <Input
                value={formData.transport_domain || ''}
                onChange={(e) => updateField('transport_domain', e.target.value)}
                placeholder="e.g., DOMAIN_XYZ"
              />
            </div>
            <div>
              <Label>Transport Group</Label>
              <Input
                value={formData.transport_group || ''}
                onChange={(e) => updateField('transport_group', e.target.value)}
                placeholder="e.g., GRP001"
              />
            </div>
            <Alert>
              <AlertDescription className="text-xs">
                Halo will automatically create transport requests and move code through your landscape based on these settings.
              </AlertDescription>
            </Alert>
          </TabsContent>
        </Tabs>

        <div className="flex gap-2 pt-4 border-t">
          <Button onClick={() => onSave(formData)} className="flex-1">
            Save Configuration
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
